import { useFormatter } from '@api/useFormatter'

import { BBDTag, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { isPastForever } from '@utils/general'
import { Col, Flex, Row } from 'antd'
import moment from 'moment'

import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useSecurityContext } from '../../../../../Navigation/SecurityContext'
import { useSupplyOptionContext } from '../../../../Contexts/SupplyOptionContext'
import { OrderDirectivePopover } from './OrderDirectivePopover'
import { SupplyOutagePopover } from './SupplyOutagePopover'
import { useMemo } from 'react'
import { findStoreNumberByProduct } from './util'
import { IconGasStation } from '@tabler/icons-react'
import { SupplyOwnerMap } from '../../../ControlPane/PaneComponents/SupplyFilters/types'

type SupplyOptionRowProps = {
  supplyOwnerMap: SupplyOwnerMap | undefined
  price: Gravitate.SupplyOptionPrice
  isCarrier?: boolean
  isDelivered?: boolean
}

export function SupplyOptionRow({ supplyOwnerMap, price, isCarrier, isDelivered }: SupplyOptionRowProps) {
  const { scopes } = useSecurityContext()
  const canViewPrices = !isCarrier && scopes.price_visibility

  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)

  const deliveredStyles = {
    border: '1px solid var(--theme-color-4)',
    backgroundColor: 'var(--bg-2)',
    color: 'var(--theme-color-4)',
  }

  const canViewDirectivePopover = scopes.scopes?.includes('pd.r') && scopes.scopes.includes('pd.w')
  return (
    <Row className={`price-row p-4`}>
      <Col span={24}>
        <Row>
          <Col span={6}>
            <BBDTag
              className='option-product-tag'
              theme2={!isDelivered}
              {...(isDelivered && { style: deliveredStyles })}
            >
              <Texto category='p2' weight={'bold'} style={{ color: 'var(--theme-color-2)' }}>
                {price.product_name || price.product}
              </Texto>
            </BBDTag>
          </Col>
          <Col span={7}>
            <Texto className='option-data-header' weight={'bold'}>
              TERMINAL
            </Texto>
          </Col>
          <Col span={6}>
            <Texto className='option-data-header' weight={'bold'}>
              SUPPLY
            </Texto>
          </Col>
          <Col span={5}>
            <Texto className='option-data-header' weight={'bold'}>
              {isCarrier ? 'PRICE WINDOW' : 'PRICE'}
            </Texto>
          </Col>
        </Row>
        {price.components.map((component, i) => {
          const productName = component.product_name || component.product
          const showBlendCode = price.blend_code && i === 0
          const contractDisplay = component.contract ? ` (${component.contract})` : ''
          const showPopover = canViewDirectivePopover && component.directive_info
          const storeNumbers = findStoreNumberByProduct(supplyOwnerMap, component, price.product_name)

          return (
            <Row key={i}>
              <Col span={6} className='flex mt-1' style={{ flexDirection: 'column', gap: '0.5em' }}>
                <Horizontal verticalCenter gap={4}>
                  <IconGasStation size={18} color='var(--theme-color-2)' />
                  <Texto category='p2' style={{ color: 'var(--theme-color-2)' }} weight={'bold'}>
                    {Array.from(storeNumbers).join(', ')}
                  </Texto>
                </Horizontal>
                <Flex vertical style={{ maxWidth: '170px', gap: '0.5em', flex: 1, justifyContent: 'flex-end' }}>
                  {showBlendCode && (
                    <Texto textTransform='uppercase' align='center' weight={600}>
                      ({price.blend_code})
                    </Texto>
                  )}
                  {showPopover && (
                    <OrderDirectivePopover supplyOptionDirectiveInfo={component.directive_info}>
                      {component.directive_info.alert ? (
                        <GraviButton
                          icon={<WarningOutlined />}
                          error
                          appearance='outlined'
                          style={{ backgroundColor: 'var(--theme-error-dim)' }}
                          buttonText='Directive Exceeded'
                          size='small'
                        />
                      ) : (
                        <GraviButton
                          icon={<InfoCircleOutlined />}
                          appearance='filled'
                          buttonText='Directive Info'
                          size='small'
                          style={{ width: 'fit-content' }}
                        />
                      )}
                    </OrderDirectivePopover>
                  )}
                  {component.outage_info?.alert === 'error' && component.outage_info?.error_outages?.length && (
                    <SupplyOutagePopover outageType='error' outages={component.outage_info.error_outages} />
                  )}
                  {component.outage_info?.alert === 'warning' && component.outage_info?.warning_outages?.length && (
                    <SupplyOutagePopover outageType='warning' outages={component.outage_info.warning_outages} />
                  )}
                </Flex>
              </Col>
              <Col span={7} className='pt-1'>
                <OptionData isPrimary value={component.terminal} />
                <OptionData label='Supplier' value={component.supplier} />
                <OptionData label='Load #' value={component.load_numbers} />
              </Col>
              <Col span={6} className='pt-1'>
                <OptionData isPrimary value={productName} />
                <OptionData label='Supply Owner' value={component.supply_owner} />
                <OptionData label='Price Type' value={component.price_type + contractDisplay} />
              </Col>
              <Col span={5} className='pt-1'>
                {canViewPrices && <OptionData isPrimary value={`${formattedCurrency}${component.price.toFixed(4)}`} />}

                <OptionData
                  isPrimary={isCarrier}
                  label='Effective'
                  value={`${formatters.DATE_TIME(moment(component.effective_from), component.effective_from_local)} -`}
                />
                <OptionData
                  isPrimary={isCarrier}
                  value={
                    isPastForever(component.effective_to)
                      ? 'Forever'
                      : formatters.DATE_TIME(moment(component.effective_to), component.effective_to_local)
                  }
                />
              </Col>
            </Row>
          )
        })}
      </Col>
    </Row>
  )
}

type OptionDataProps = {
  isPrimary?: boolean
  value: string | string[]
  label?: string
}

function OptionData({ isPrimary, value, label }: OptionDataProps) {
  const dataClass = isPrimary ? 'option-data-primary' : 'option-data-secondary'
  if (!value || (Array.isArray(value) && value.length === 0)) return null
  return (
    <Texto category={isPrimary ? 'p2' : 'label'} weight={isPrimary ? 'bold' : 'normal'} className={dataClass}>
      {label ? <b>{label}: </b> : null}
      {Array.isArray(value) ? value.join(', ') : value}
    </Texto>
  )
}

export function PriceWarning({ option, isCarrier }) {
  const { selectedStatus } = useSupplyOptionContext()
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)

  if (!option || isCarrier) return null

  if (selectedStatus === 'invalid') {
    return (
      <Texto category='p2' className='pl-4 pt-1 pb-4' appearance='error' weight='600'>
        <b> Supply Option Invalid: </b>This supply option is{' '}
        {option.missingStatus ? option.missingStatus.toLowerCase() : 'no longer applicable for this order'}.
      </Texto>
    )
  }
  if (selectedStatus === 'money') {
    return (
      <Texto category='p2' className='pl-4 pt-1 pb-4' appearance='success' weight='600'>
        <b> Arbitrage Opportunity: </b>This supply option is{' '}
        <b>
          {formattedCurrency}
          {option.priceDifference}
        </b>{' '}
        more expensive than an alternative supply at <b>{option.optimalTerminal}</b> through driving{' '}
        <b>{option.loadedMiles}. </b>
      </Texto>
    )
  }
  if (selectedStatus === 'warning') {
    return (
      <Texto appearance='warning' className='pl-4 pt-1 pb-4' category='p2' weight='600'>
        This supply option is{' '}
        <b>
          {formattedCurrency}
          {option.priceDifference}
        </b>{' '}
        more expensive than an alternative supply for this order. {selectedStatus}
      </Texto>
    )
  }
  return (
    <Texto appearance='error' className='pl-4 pt-1 pb-4' category='p2' weight='600'>
      This supply option is{' '}
      <b>
        {formattedCurrency}
        {option.priceDifference}
      </b>{' '}
      more expensive than an alternative supply for this order.
    </Texto>
  )
}
